import { Box, Button, CircularProgress, LinearProgress, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { enUS as en, esES as es, ptBR } from "@mui/x-data-grid/locales";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../contexts/AuthContext";
import MyModal from "../MyModal";
import { ILoyaltyBatchError, loyaltyService } from "../../services/loyaltyService";

interface IModalBatchErrors {
  jobId: string;
  jobLabel?: string;
  modalChange: (open: boolean) => void;
}

const headerSx = {
  "& .header-style": {
    backgroundColor: "var(--gray-100)",
    minHeight: "4rem",
  },
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "700 !important" },
};

export default function ModalBatchErrors({
  jobId,
  jobLabel,
  modalChange,
}: IModalBatchErrors) {
  const { t, i18n } = useTranslation();
  const {
    tokenHeaders,
    country: { countryCode },
  } = useContext(AuthContext);

  const languages: { [key: string]: any } = { ptbr: ptBR, en, es };
  const currentLocale = languages[i18n.language] ?? en;
  const localeText =
    currentLocale.components.MuiDataGrid.defaultProps.localeText;

  const [rows, setRows] = useState<ILoyaltyBatchError[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      try {
        const headers = {
          ...tokenHeaders,
          headers: { ...tokenHeaders.headers, countryCode },
        };
        const params: Record<string, any> = { page, size: pageSize };
        if (search) params.search = search;
        const { data } = await loyaltyService.getBatchErrors(jobId, params, headers);
        setRows(data.content);
        setTotal(data.totalElements);
      } catch {
        setRows([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, jobId, search]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  const handleClear = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const columns: GridColDef[] = [
    {
      field: "errorMessage",
      headerName: t("loyalty.groups.errors.columns.errorMessage"),
      flex: 1,
      minWidth: 250,
      headerClassName: "header-style",
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  return (
    <MyModal
      title={t("loyalty.groups.errors.title", { groupName: jobLabel ?? "" })}
      modalChange={modalChange}
      width="90%"
      maxWidth="800px"
    >
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t("loyalty.groups.errors.searchLabel")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            size="small"
            placeholder={t("loyalty.groups.searchLabel")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ flex: 1, maxWidth: 360 }}
          />
          <Button variant="contained" onClick={handleSearch}>
            {t("loyalty.groups.searchButton")}
          </Button>
          <Button sx={{ color: "#00AFF2" }} variant="outlined" onClick={handleClear}>
            {t("loyalty.groups.cleanButton")}
          </Button>
        </Box>
      </Box>

      {isLoading && !rows.length ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={headerSx}>
          <DataGrid
            rows={rows}
            columns={columns}
            rowCount={total}
            loading={isLoading}
            getRowId={(row) => String(row.rowNumber)}
            pageSizeOptions={[25, 50, 75, 100]}
            pagination
            paginationMode="server"
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            localeText={localeText}
            slots={{ loadingOverlay: LinearProgress as any }}
            autoHeight
            disableColumnFilter
            disableRowSelectionOnClick
            sx={{ "& .MuiDataGrid-row": { cursor: "default" } }}
          />
        </Box>
      )}
    </MyModal>
  );
}
